import type { Ref } from 'vue'
import { serverClockOffset, serverNow } from '~/utils/time'

/**
 * 服务端校正后的「此刻」（响应式）。
 *
 * 时差由 API 拦截器按响应头 X-Server-Time 维护（往返最短的样本），见 utils/time.ts 的 serverNow()。
 * 同一刷新间隔的所有组件共用一个定时器，按服务端时钟对齐整拍刷新；切回前台立即刷新。
 * 服务端渲染不跑钟（now 固定为渲染时刻）；需要与水合一致的首屏请用 useTimeDisplay。
 */
interface Ticker {
  interval: number
  now: Ref<number>
  users: number
  timer: ReturnType<typeof setTimeout> | null
}

const tickers = new Map<number, Ticker>()
let visibilityBound = false

function schedule(ticker: Ticker) {
  const now = serverNow()
  ticker.now.value = now
  // 对齐到整拍（如整秒），各倒计时 / 列表同一拍翻页；多等 16ms 保证越过边界
  const wait =
    ticker.interval - (((now % ticker.interval) + ticker.interval) % ticker.interval) + 16
  ticker.timer = setTimeout(() => schedule(ticker), wait)
}

function bindVisibility() {
  if (visibilityBound) return
  visibilityBound = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    for (const ticker of tickers.values()) {
      if (ticker.timer === null) continue
      clearTimeout(ticker.timer)
      schedule(ticker)
    }
  })
}

export function useServerClock(intervalMs = 1000) {
  if (!import.meta.client) {
    return { now: readonly(ref(serverNow())), serverNow, offset: serverClockOffset }
  }

  let ticker = tickers.get(intervalMs)
  if (!ticker) {
    ticker = { interval: intervalMs, now: ref(serverNow()), users: 0, timer: null }
    tickers.set(intervalMs, ticker)
  }
  const shared = ticker
  if (shared.timer === null) shared.now.value = serverNow()

  onMounted(() => {
    bindVisibility()
    shared.users += 1
    if (shared.timer === null) schedule(shared)
  })

  onBeforeUnmount(() => {
    shared.users -= 1
    if (shared.users > 0 || shared.timer === null) return
    clearTimeout(shared.timer)
    shared.timer = null
  })

  return { now: readonly(shared.now), serverNow, offset: serverClockOffset }
}
